import React, { useState, useEffect, useCallback, useRef } from 'react';
import client from '../../api/client';
import { uploadFile } from '../../utils/upload';
import {
  Plus, Edit3, Trash2, Save, Check, ArrowLeft, ChevronUp, ChevronDown,
  Upload, Image as ImageIcon, Images
} from 'lucide-react';
import { toast, confirmDialog } from '../../components/Toast/toast';

export default function SitePhotosAdmin() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | id
  const [form, setForm] = useState({ image_url: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    client.get('/admin/site/photos')
      .then(res => setPhotos(res.data || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Сохранение нового порядка после перестановки
  const saveOrder = async (reordered) => {
    try {
      await Promise.all(
        reordered.map((photo, index) =>
          client.put(`/admin/site/photos/${photo.id}`, {
            image_url: photo.image_url,
            sort_order: index,
          })
        )
      );
    } catch (err) {
      console.error('Ошибка сохранения порядка:', err);
      toast.error('Не удалось сохранить порядок');
    }
  };

  const movePhoto = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= photos.length) return;
    const arr = [...photos];
    [arr[index], arr[targetIndex]] = [arr[targetIndex], arr[index]];
    setPhotos(arr);
    saveOrder(arr);
  };

  const openAdd = () => {
    setEditing('new');
    setForm({ image_url: '', sort_order: photos.length });
    setUploadError('');
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ image_url: p.image_url || '', sort_order: p.sort_order ?? 0 });
    setUploadError('');
  };

  const closeForm = () => {
    setEditing(null);
    setForm({ image_url: '', sort_order: 0 });
    setUploadError('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const url = await uploadFile(file);
      setForm(prev => ({ ...prev, image_url: url }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setUploadError(typeof detail === 'string' ? detail : 'Не удалось загрузить фото');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.image_url) return;
    setSaving(true);
    setSaved(false);
    try {
      const payload = {
        image_url: form.image_url,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editing === 'new') {
        await client.post('/admin/site/photos', payload);
      } else {
        await client.put(`/admin/site/photos/${editing}`, payload);
      }
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        closeForm();
        load();
      }, 800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await confirmDialog('Удалить фото?', {
      danger: true,
      okLabel: 'Удалить',
      cancelLabel: 'Отмена',
      detail: 'Фото пропадёт из слайдера на главной странице.',
      onConfirm: async () => {
        try {
          await client.delete(`/admin/site/photos/${id}`);
          load();
        } catch (err) {
          toast.error('Ошибка удаления');
          throw err;
        }
      },
    });
  };

  return (
    <div className="space-y-4">
      {/* Список — только когда редактор закрыт (форма не уезжает вниз) */}
      {editing === null && (
      <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-[#64748b]">
          {photos.length} фото · слайдер главной
        </h2>
        <button
          onClick={openAdd}
          className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#374151] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1f2937] active:scale-[0.97]"
        >
          <Plus className="h-4 w-4" />
          Добавить
        </button>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">Загрузка...</div>
      ) : photos.length === 0 ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          <Images className="mx-auto mb-2 h-10 w-10 text-[#e2e8f0]" />
          <p>Фото не добавлены</p>
          <p className="mt-1 text-xs">Они появятся в слайдере на главной странице сайта</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
          {photos.map((p, index) => (
            <div
              key={p.id}
              className="flex items-center gap-3 rounded-xl border border-[#e2e8f0] bg-white p-3 shadow-sm"
            >
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => movePhoto(index, -1)}
                  disabled={index === 0}
                  className="grid h-5 w-6 place-items-center rounded text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-[#64748b]"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => movePhoto(index, 1)}
                  disabled={index === photos.length - 1}
                  className="grid h-5 w-6 place-items-center rounded text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-[#64748b]"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-[#f1f5f9]">
                {p.image_url ? (
                  <img src={p.image_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[#e2e8f0]">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0f172a]">Позиция {index + 1}</div>
                <div className="truncate text-xs text-[#64748b]">{p.image_url}</div>
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => openEdit(p)} className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a]">
                  <Edit3 className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(p.id)} className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] hover:bg-[#D64545]/10 hover:text-[#D64545]">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {/* Редактор фото — отдельной страницей (как «Новый товар») */}
      {editing !== null && (
        <div className="space-y-4">
          <header className="rounded-2xl border border-[#e2e8f0] bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <button onClick={closeForm} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#f1f5f9] text-[#64748b] transition active:scale-90">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h2 className="min-w-0 flex-1 truncate text-sm font-bold text-[#0f172a]">
                {editing === 'new' ? 'Новое фото' : 'Редактирование'}
              </h2>
            </div>
          </header>
          <div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Фото *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-3">
                  {form.image_url ? (
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-[#e2e8f0]">
                      <img src={form.image_url} alt="preview" className="h-full w-full object-cover" />
                    </div>
                  ) : (
                    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-[#e2e8f0] text-[#e2e8f0]">
                      <ImageIcon className="h-7 w-7" />
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-2 text-xs font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-95 disabled:opacity-50"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {uploading ? 'Загрузка...' : 'Выбрать фото'}
                    </button>
                    {form.image_url && (
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, image_url: '' }))}
                        className="text-xs text-[#D64545]/70 hover:text-[#D64545]"
                      >
                        Удалить фото
                      </button>
                    )}
                  </div>
                </div>
                {uploadError && (
                  <p className="mt-1 text-xs text-[#D64545]">{uploadError}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Порядок</label>
                <input
                  type="number"
                  min="0"
                  value={form.sort_order}
                  onChange={e => setForm(prev => ({ ...prev, sort_order: e.target.value }))}
                  className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
                <p className="mt-1 text-[11px] text-[#94a3b8]">
                  Чем меньше число, тем раньше фото в слайдере. Порядок также можно менять стрелками в списке.
                </p>
              </div>
              <div className="mt-5 flex gap-2">
                <button onClick={closeForm} className="flex-1 rounded-xl border border-[#e2e8f0] py-2.5 text-sm font-medium text-[#64748b] hover:bg-[#f8fafc]">
                  Отмена
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || saved || !form.image_url || uploading}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold transition active:scale-[0.98] disabled:opacity-50 ${
                    saved
                      ? 'bg-[#6B9B74]/15 text-[#6B9B74]'
                      : 'bg-[#374151] text-white hover:bg-[#1f2937]'
                  }`}
                >
                  {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                  {saved ? 'Сохранено!' : saving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
